import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { v4 as uuid } from 'uuid';
import { enhanceGLBridge, filterBridge, fixZipResUrl, makeupBridge } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { getFramework, IFramework, type IGetSrcImage } from '@/framework';
import { type MainLayer } from '@/layers/main-layer';
import { applyRenderedMainLayerURL } from '@/layers/main-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { IImageLayerProps, IImageProps, ILayerProps } from '@/version-types';
import { getImageEnhanceState, IStore, watchImageEnhanceChange } from '../states/image-enhance-state';
import { IMakeUpEnhance, IMakeUpFilter } from '../types';
import { getHslArgs, getPosterizeArgs } from '../utils';
import { adapterEnhanceArgs } from '../utils/enhance';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

const filterAssets = {
    filterPath: 'filter/configuration_filter.plist',
};

export class MakeupImageEnhance {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEnhanceState(identity);

        watchImageEnhanceChange(identity, (state) => {
            this.store = state;
        });
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.service.registration.getInstance<MainLayer>('MainLayer');
        if (!mainLayer) {
            throw new Error('MainLayer 实例不存在');
        }
        return mainLayer;
    }

    async create(
        _originLayer: ILayerProps,
        params: {
            filterID: string;
            filterURL: string;
            filterPath: string;
            isNeedInit: boolean;
            alpha: number;
            enhanceData: any;
        } = {
            filterID: '',
            filterURL: '',
            isNeedInit: true,
            filterPath: filterAssets.filterPath,
            alpha: 1,
            enhanceData: {},
        },
    ) {
        const { filterID, filterURL, isNeedInit, filterPath, enhanceData } = params;
        const originLayer = await this.tryGetMainLayer().enterMergeMode(_originLayer);

        this.store.setOriginLayer(originLayer);
        makeupBridge.setFilterBridgeInfo({
            order: 1,
            bridge: filterBridge,
            filterID,
            filterURL,
            alpha: 1,
            isNeedInit,
            filterPath,
        });

        makeupBridge.setEnhanceGLBridgeInfo({
            bridge: enhanceGLBridge,
            order: 0,
            filterID: assets.id,
            filterURL: fixZipResUrl(assets.zip),
            params: {
                ...enhanceData,
            },
            enhanceHSLMode: 0,
            isNeedInit: true,
        });

        console.log('===>create', { originLayer });

        // 初始化gl数据
        const obj = this.framework.service.edgelessCanvas.getByLayerId(originLayer.id) as unknown as IGetSrcImage;
        if (!obj || !obj.getSrcImage) {
            throw new Error('===>未找到对应的无限画布对象');
        }
        const rawCanvas = await obj.getSrcImage();

        const fromCanvas = glKit.scaleResourceIfNeeded(rawCanvas);

        await makeupBridge.from(fromCanvas).apply();
    }

    async resetEnhance() {
        this.store.resetEnhance();
    }

    async reset(pushHistory = true, trackFunc?: string): Promise<void> {
        const { framework, store: enhanceState } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        const originLayer = enhanceState.originLayer;
        const oldLayer = framework.service.frame.getMainLayer(originIndex);
        const newLayer = cloneDeep(originLayer);

        if (!newLayer || !oldLayer) return;

        newLayer.__extendedState = {
            ...newLayer.__extendedState,
            makeupPresetId: undefined,
        };

        framework.service.frame.addOrUpdateLayer(newLayer);

        if (!pushHistory) return;

        this.framework.service.history.write(oldLayer, newLayer, trackFunc);
    }

    async batchReset(pushHistory = true, trackFunc?: string): Promise<void> {
        if (this.store.lastOriginLayers.length === 0) {
            return;
        }

        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = this.store.originLayers[i];
            if (!mainLayer) {
                continue;
            }

            if (i === 0) {
                mainLayer.__extendedState = {
                    ...mainLayer.__extendedState,
                    makeupPresetId: undefined,
                };
            }

            this.framework.service.frame.addOrUpdateLayer(cloneDeep(mainLayer));
        }

        if (pushHistory) {
            this.framework.service.history.write(
                cloneDeep(this.store.lastOriginLayers),
                cloneDeep(this.store.originLayers),
                trackFunc,
            );
        }
        this.store.setLastOriginLayers(cloneDeep(this.store.originLayers));
    }

    private async setFilter(filter: IMakeUpFilter) {
        console.log('setFilter', filter);
        makeupBridge.setFilterBridgeInfo(filter as any);
    }

    private async setEnhance(params: IMakeUpEnhance) {
        const { store: enhanceState } = this;
        const { baseEnhance, light, color, particulars } = params;
        const baseData = enhanceState.baseEnhance;
        const colorData = enhanceState.colorEnhance;
        const lightData = enhanceState.lightEnhance;
        const particularsData = enhanceState.particularsEnhance;
        const posterizeData = enhanceState.posterizeEnhance;
        const hslData = enhanceState.hslEnhance;
        const args = {};

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            baseEnhance,
            { light },
            { color },
            { particulars },
        );
        console.log('setEnhance Action', { args, params });
        const adapterParams = adapterEnhanceArgs(args);
        makeupBridge.setEnhanceGLBridgeInfo({ params: adapterParams } as any);
    }

    // FIXME: 首次点击预设调色没有效果
    async action(
        preset: {
            enhance: IMakeUpEnhance;
            filter: IMakeUpFilter;
            id: number;
        },
        pushHistory = false,
        trackFunc?: string,
    ): Promise<void> {
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();

        // TODO: 支持非主图的场景
        const oldLayer = cloneDeep(framework.service.frame.getMainLayer(originIndex)!);
        const finalLayer = cloneDeep(oldLayer);
        finalLayer.__extendedState = {
            ...finalLayer.__extendedState,
            makeupPresetId: preset.id,
        };

        if (preset.enhance) this.setEnhance(preset.enhance);
        if (preset.filter) this.setFilter(preset.filter);

        const data = finalLayer.data as IImageProps;
        glKit.openGL(finalLayer);
        glKit.processGL(finalLayer);

        await glKit.applyGLResult({
            layer: finalLayer,
            props: data,
            baseBridge: makeupBridge,
            resultSize: {
                width: data.naturalWidth,
                height: data.naturalHeight,
            },
            beforeGetGLResult: async () => {
                // 为了复用 apply 获取到的结果 canvas
                return (await makeupBridge.apply()).canvas;
            },
        });

        const latestLayer = framework.service.flatLayer.getLayer(finalLayer.id, true);
        if (!latestLayer) return;
        const committedLayer = applyRenderedMainLayerURL(latestLayer, (finalLayer.data as IImageProps).url);
        committedLayer.__extendedState = {
            ...committedLayer.__extendedState,
            makeupPresetId: preset.id,
        };
        framework.service.frame.addOrUpdateLayer(committedLayer);

        const mergeResult = this.tryGetMainLayer().applyMergeMode(committedLayer, trackFunc);

        if (mergeResult || !pushHistory) return;

        // 历史快照只保留正式图片，避免撤销后继续读取当前 GL 预览。
        glKit.clearGL(latestLayer);
        this.framework.service.history.write(latestLayer, committedLayer, trackFunc);
    }

    async batchAction(
        preset: {
            enhance: IMakeUpEnhance;
            filter: IMakeUpFilter;
            id: number;
        },
        pushHistory = false,
        trackFunc?: string,
        onStageLoading?: (stageIndex: number, loadingInfo: { loading: boolean; text: string }) => void,
    ): Promise<void> {
        const { framework } = this;
        const newLayers = [] as ILayerProps[];
        const oldLayers = [] as ILayerProps[];
        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = cloneDeep(this.store.originLayers[i]);
            if (!mainLayer) {
                continue;
            }
            if (i === 0) {
                mainLayer.__extendedState = {
                    ...mainLayer.__extendedState,
                    makeupPresetId: preset.id,
                };
            }
            oldLayers.push(cloneDeep(this.store.lastOriginLayers[i] || cloneDeep(mainLayer)));
            const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
            const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
            console.log('===>batchAction', (mainLayer.data as IImageLayerProps).url);
            const { canvas } = createCanvas(
                naturalWidth * imageTransform.scale,
                naturalHeight * imageTransform.scale,
                img,
            );
            await makeupBridge.from(canvas);
            if (preset.enhance) {
                this.setEnhance(preset.enhance);
            }
            if (preset.filter) {
                this.setFilter(preset.filter);
            }

            const { canvas: resultCanvas } = await makeupBridge.apply();

            if (resultCanvas) {
                const base64 = resultCanvas.toDataURL();
                const data = mainLayer.data as IImageLayerProps;

                data.url = base64Kit.getCode(base64, uuid());
            }
            newLayers.push(cloneDeep(mainLayer));
            framework.service.frame.addOrUpdateLayer(cloneDeep(mainLayer));
            onStageLoading?.(framework.service.frame.getLayerFrameId(mainLayer.id) || 0, { loading: false, text: '' });
        }

        if (pushHistory) {
            this.store.setLastOriginLayers(cloneDeep(newLayers));
            this.framework.service.history.write(oldLayers, newLayers, trackFunc);
        }
    }
}
